<script>
	import { fly, fade } from 'svelte/transition';
	import Icon from './lib/Icon.svelte';
	import { TUTORIALS, TUTORIAL_CATEGORIES } from './lib/tutorials.js';

	let { onNavigate = (_view) => {} } = $props();

	const SEEN_KEY = 'mangu_tutorials_seen';

	function loadSeen() {
		try {
			const raw = localStorage.getItem(SEEN_KEY);
			const arr = raw ? JSON.parse(raw) : [];
			return Array.isArray(arr) ? arr : [];
		} catch {
			return [];
		}
	}

	let currentId = $state(null);
	let seen = $state(loadSeen());

	let current = $derived(TUTORIALS.find(t => t.id === currentId) || null);
	let currentCat = $derived(
		current ? TUTORIAL_CATEGORIES.find(c => c.id === current.category) || null : null
	);
	let byCategory = $derived(
		TUTORIAL_CATEGORIES
			.map(c => ({ ...c, items: TUTORIALS.filter(t => t.category === c.id) }))
			.filter(c => c.items.length > 0)
	);
	let progress = $derived(
		TUTORIALS.length ? Math.round((seen.length / TUTORIALS.length) * 100) : 0
	);

	function persist() {
		try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch {}
	}
	function markSeen(id) {
		if (seen.includes(id)) return;
		seen = [...seen, id];
		persist();
	}
	function resetProgress() {
		seen = [];
		persist();
	}
	function open(id) { currentId = id; }
	function back() { currentId = null; }
	function goTo(view) { onNavigate(view); }
</script>

<div class="tut-view">
	{#if !current}
		<!-- Índice -->
		<header class="hero-index">
			<div class="hero-top">
				<div>
					<h2 class="title">Tutoriales</h2>
					<p class="muted">Aprende a usar cada sección de la app, paso a paso.</p>
				</div>
				{#if seen.length > 0}
					<button class="reset" onclick={resetProgress}>
						<Icon name="refresh" size={14} /> Reiniciar progreso
					</button>
				{/if}
			</div>
			<div class="progress">
				<div class="progress-bar"><div class="progress-fill" style="width:{progress}%"></div></div>
				<span class="progress-label">{seen.length}/{TUTORIALS.length} completados · {progress}%</span>
			</div>
		</header>

		<div class="body">
			{#each byCategory as cat (cat.id)}
				<section class="cat">
					<div class="cat-head">
						<span class="cat-icon"><Icon name={cat.icon} size={16} /></span>
						<h3>{cat.label}</h3>
						<span class="cat-count">{cat.items.length}</span>
					</div>
					<div class="grid">
						{#each cat.items as t, i (t.id)}
							<button class="card" onclick={() => open(t.id)} in:fly={{ y: 8, duration: 180, delay: i * 40 }}>
								<div class="card-top">
									<span class="card-icon"><Icon name={t.icon} size={18} /></span>
									{#if seen.includes(t.id)}
										<span class="seen"><Icon name="check-circle" size={16} /></span>
									{/if}
								</div>
								<h4>{t.title}</h4>
								<p>{t.summary}</p>
								<div class="card-foot">
									<span class="meta"><Icon name="list" size={12} /> {t.sections.length} secciones</span>
									<span class="meta">~{t.minutes} min</span>
								</div>
							</button>
						{/each}
					</div>
				</section>
			{/each}
		</div>
	{:else}
		<!-- Detalle -->
		{#key current.id}
			<div class="detail-wrap" in:fly={{ x: 14, duration: 200 }}>
				<div class="detail-bar">
					<button class="back" onclick={back}><Icon name="arrow-left" size={16} /> Todos los tutoriales</button>
				</div>

				<div class="detail-hero">
					<span class="detail-icon"><Icon name={current.icon} size={26} /></span>
					<div class="detail-heading">
						{#if currentCat}<span class="detail-topic">{currentCat.label}</span>{/if}
						<h2>{current.title}</h2>
						<p class="muted">{current.intro}</p>
						<div class="detail-meta">
							<span><Icon name="list" size={12} /> {current.sections.length} secciones</span>
							<span>~{current.minutes} min de lectura</span>
						</div>
					</div>
				</div>

				<ol class="steps">
					{#each current.sections as sec, i}
						<li in:fade={{ duration: 160, delay: 50 + i * 45 }}>
							<span class="step-num">{i + 1}</span>
							<div class="step-text">
								<h4>{sec.title}</h4>
								<p>{sec.body}</p>
								{#if sec.subs && sec.subs.length}
									<ul class="subs">
										{#each sec.subs as sub}
											<li><b>{sub.title}.</b> {sub.body}</li>
										{/each}
									</ul>
								{/if}
								{#if sec.tip}
									<div class="tip">
										<Icon name="sparkles" size={14} />
										<span>{sec.tip}</span>
									</div>
								{/if}
							</div>
						</li>
					{/each}
				</ol>

				<div class="detail-cta">
					<button class="btn btn-primary" onclick={() => goTo(current.cta.view)}>
						{current.cta.label}
						<Icon name="arrow-right" size={14} />
					</button>
					{#if seen.includes(current.id)}
						<span class="done"><Icon name="check-circle" size={16} /> Completado</span>
					{:else}
						<button class="btn-ghost" onclick={() => markSeen(current.id)}>
							<Icon name="check" size={15} /> Marcar como visto
						</button>
					{/if}
				</div>
			</div>
		{/key}
	{/if}
</div>

<style>
	.tut-view { height: 100%; display: flex; flex-direction: column; background: var(--surface-1); overflow-y: auto; }

	/* ── Índice ── */
	.hero-index {
		padding: 20px 24px 16px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.hero-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
	.title { font-size: 18px; margin: 0 0 4px; }
	.muted { color: var(--text-3); font-size: 12.5px; line-height: 1.55; max-width: 620px; margin: 0; }
	.reset {
		display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0;
		font-size: 12px; font-weight: 600; color: var(--text-2);
		padding: 6px 10px; border-radius: 7px;
	}
	.reset:hover { background: var(--surface-2); color: var(--text-1); }
	.progress { display: flex; align-items: center; gap: 12px; margin-top: 14px; }
	.progress-bar { flex: 1; height: 7px; background: var(--surface-2); border-radius: 999px; overflow: hidden; }
	.progress-fill { height: 100%; background: var(--accent); border-radius: 999px; transition: width var(--transition-fast); }
	.progress-label { font-size: 11.5px; font-weight: 600; color: var(--text-2); flex-shrink: 0; }

	.body { padding: 18px 24px 28px; }
	.cat { margin-bottom: 26px; }
	.cat-head { display: flex; align-items: center; gap: 9px; margin-bottom: 12px; }
	.cat-icon {
		width: 28px; height: 28px; border-radius: 8px;
		background: var(--accent-soft); color: var(--accent);
		display: flex; align-items: center; justify-content: center;
	}
	.cat-head h3 { font-size: 14px; margin: 0; color: var(--text-1); }
	.cat-count {
		font-size: 11px; font-weight: 700; color: var(--text-3);
		background: var(--surface-2); padding: 1px 8px; border-radius: 999px;
	}
	.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
	.card {
		display: flex; flex-direction: column; gap: 8px;
		padding: 15px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 12px;
		text-align: left; cursor: pointer;
		transition: border-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast);
	}
	.card:hover { border-color: var(--accent); box-shadow: var(--shadow-sm); transform: translateY(-2px); }
	.card-top { display: flex; align-items: center; justify-content: space-between; }
	.card-icon {
		width: 38px; height: 38px; border-radius: 10px;
		background: var(--accent-soft); color: var(--accent);
		display: flex; align-items: center; justify-content: center;
	}
	.seen { color: var(--accent); display: flex; }
	.card h4 { font-size: 14px; margin: 2px 0 0; color: var(--text-1); }
	.card p { font-size: 12px; line-height: 1.5; color: var(--text-3); margin: 0; flex: 1; }
	.card-foot { display: flex; align-items: center; gap: 12px; margin-top: 2px; }
	.meta { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: var(--text-3); }

	/* ── Detalle ── */
	.detail-wrap { max-width: 760px; margin: 0 auto; padding: 16px 24px 40px; width: 100%; box-sizing: border-box; }
	.detail-bar { margin-bottom: 14px; }
	.back {
		display: inline-flex; align-items: center; gap: 6px;
		font-size: 13px; font-weight: 600; color: var(--text-2);
		padding: 6px 10px; border-radius: 7px; margin-left: -10px;
	}
	.back:hover { background: var(--surface-2); color: var(--text-1); }

	.detail-hero {
		display: flex; gap: 16px; align-items: flex-start;
		padding: 20px; margin-bottom: 22px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 14px;
	}
	.detail-icon {
		width: 54px; height: 54px; flex-shrink: 0; border-radius: 13px;
		background: var(--accent-soft); color: var(--accent);
		display: flex; align-items: center; justify-content: center;
	}
	.detail-topic {
		font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
		color: var(--accent);
	}
	.detail-heading h2 { font-size: 19px; margin: 3px 0 7px; color: var(--text-1); }
	.detail-meta { display: flex; gap: 16px; margin-top: 12px; }
	.detail-meta span { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; color: var(--text-3); }

	.steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 18px; }
	.steps > li { display: flex; gap: 14px; align-items: flex-start; }
	.step-num {
		width: 30px; height: 30px; flex-shrink: 0; border-radius: 50%;
		background: var(--accent); color: #fff;
		display: flex; align-items: center; justify-content: center;
		font-size: 13px; font-weight: 700;
	}
	.step-text { padding-top: 3px; flex: 1; }
	.step-text h4 { font-size: 14.5px; margin: 0 0 5px; color: var(--text-1); }
	.step-text p { font-size: 13px; line-height: 1.6; color: var(--text-2); margin: 0; }
	.subs { margin: 8px 0 0; padding-left: 18px; }
	.subs li { font-size: 12.5px; line-height: 1.55; color: var(--text-2); margin-bottom: 4px; }
	.tip {
		display: flex; align-items: flex-start; gap: 8px;
		margin-top: 10px; padding: 10px 12px;
		background: var(--accent-soft);
		border-left: 3px solid var(--accent);
		border-radius: 8px;
		font-size: 12.5px; line-height: 1.55; color: var(--text-1);
	}
	.tip :global(svg) { color: var(--accent); flex-shrink: 0; margin-top: 1px; }

	.detail-cta { display: flex; align-items: center; gap: 14px; margin-top: 28px; }
	.btn-ghost {
		display: inline-flex; align-items: center; gap: 6px;
		font-size: 13px; font-weight: 600; color: var(--text-2);
		padding: 8px 12px; border: 1px solid var(--border-1); border-radius: 9px;
	}
	.btn-ghost:hover { background: var(--surface-2); color: var(--text-1); border-color: var(--accent); }
	.done { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: var(--accent); }
</style>
